import React, { useState, useEffect } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import {
  Plus,
  Users,
  Edit3,
  UserX,
  UserCheck,
  Trash2,
  AlertTriangle,
  X,
  Mail,
  Phone,
  Clock,
} from "lucide-react";
import { API } from "../utils/constants";

const UsersPage = () => {
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [usersError, setUsersError] = useState(null);
  const [showAddUser, setShowAddUser] = useState(false);
  const [showEditUser, setShowEditUser] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userForm, setUserForm] = useState({
    name: '',
    surname: '',
    email: '',
    mobile: '',
    company: '',
    role: 'Property Manager',
    password: ''
  });
  const [userFormLoading, setUserFormLoading] = useState(false);
  const [deleteConfirmUser, setDeleteConfirmUser] = useState(null);

  const fetchUsers = async () => {
    try {
      setUsersLoading(true);
      setUsersError(null);
      const response = await axios.get(`${API}/auth/users`);
      setUsers(response.data || []);
    } catch (error) {
      console.error('Error fetching users:', error);
      setUsersError('Failed to load users');
      if (error.response?.status === 403) {
        setUsersError('Insufficient permissions to view users');
      } else {
        setUsers([
          {
            id: "1",
            name: "Test",
            surname: "Property Manager",
            email: "test_property_manager@fortress.com",
            mobile: "1234567890",
            company: "Test Company",
            role: "Property Manager",
            is_active: true,
            created_at: new Date().toISOString(),
            last_login: new Date().toISOString()
          },
          {
            id: "2",
            name: "Test",
            surname: "Asset Manager",
            email: "test_asset_manager@fortress.com",
            mobile: "1234567890",
            company: "Test Company",
            role: "Asset Manager",
            is_active: true,
            created_at: new Date().toISOString(),
            last_login: null
          }
        ]);
      }
    } finally {
      setUsersLoading(false);
    }
  };

  const createUser = async () => {
    try {
      setUserFormLoading(true);

      if (!userForm.name.trim() || !userForm.surname.trim() || !userForm.email.trim() || !userForm.password.trim()) {
        toast.error('Please fill in all required fields');
        return;
      }

      if (userForm.password.length < 6) {
        toast.error('Password must be at least 6 characters');
        return;
      }

      const response = await axios.post(`${API}/auth/users`, userForm);
      setUsers(prev => [response.data, ...prev]);

      setUserForm({
        name: '',
        surname: '',
        email: '',
        mobile: '',
        company: '',
        role: 'Property Manager',
        password: ''
      });

      setShowAddUser(false);
      toast.success('User created successfully!');
    } catch (error) {
      console.error('Error creating user:', error);
      if (error.response?.status === 400 && error.response?.data?.detail?.includes('already exists')) {
        toast.error('User with this email already exists');
      } else if (error.response?.status === 403) {
        toast.error('Insufficient permissions to create users');
      } else {
        toast.error('Failed to create user');
      }
    } finally {
      setUserFormLoading(false);
    }
  };

  const editUser = (user) => {
    setSelectedUser(user);
    setUserForm({
      name: user.name,
      surname: user.surname,
      email: user.email,
      mobile: user.mobile || '',
      company: user.company || '',
      role: user.role,
      password: ''
    });
    setShowEditUser(true);
  };

  const updateUser = async () => {
    try {
      setUserFormLoading(true);

      const updateData = { ...userForm };
      if (!updateData.password.trim()) {
        delete updateData.password;
      }

      const response = await axios.put(`${API}/auth/users/${selectedUser.id}`, updateData);
      setUsers(prev => prev.map(u => u.id === selectedUser.id ? response.data : u));

      setShowEditUser(false);
      toast.success('User updated successfully!');
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error('Failed to update user');
    } finally {
      setUserFormLoading(false);
    }
  };

  const toggleUserActive = async (user) => {
    const isCurrentlyActive = user.is_active !== false;
    const action = isCurrentlyActive ? 'deactivate' : 'reactivate';
    if (!window.confirm(`Are you sure you want to ${action} this user?`)) {
      return;
    }

    try {
      if (isCurrentlyActive) {
        await axios.delete(`${API}/auth/users/${user.id}`);
        setUsers(prev => prev.map(u => u.id === user.id ? { ...u, is_active: false } : u));
        toast.success('User deactivated successfully!');
      } else {
        await axios.post(`${API}/auth/users/${user.id}/reactivate`);
        setUsers(prev => prev.map(u => u.id === user.id ? { ...u, is_active: true } : u));
        toast.success('User reactivated successfully!');
      }
    } catch (error) {
      console.error(`Error ${action}ing user:`, error);
      toast.error(`Failed to ${action} user`);
    }
  };

  const hardDeleteUser = async (userId) => {
    try {
      await axios.delete(`${API}/auth/users/${userId}/permanent`);
      setUsers(prev => prev.filter(u => u.id !== userId));
      toast.success('User permanently deleted');
    } catch (error) {
      console.error('Error deleting user:', error);
      if (error.response?.status === 403) {
        toast.error(error.response.data?.detail || 'Cannot delete this user');
      } else {
        toast.error('Failed to delete user');
      }
    } finally {
      setDeleteConfirmUser(null);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleUserFormChange = (e) => {
    setUserForm(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const resetUserForm = () => {
    setUserForm({
      name: '',
      surname: '',
      email: '',
      mobile: '',
      company: '',
      role: 'Property Manager',
      password: ''
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <Users className="mr-3 h-6 w-6 text-cyan-600" />
            User Management
          </h1>
          <p className="text-gray-600 mt-1">
            {users.length} user{users.length !== 1 ? 's' : ''} registered
          </p>
        </div>

        <button
          onClick={() => {
            resetUserForm();
            setShowAddUser(true);
          }}
          className="text-white px-4 py-2 rounded-lg hover:opacity-90 flex items-center gap-2 transition-opacity"
          style={{ backgroundColor: '#00C5CB' }}
        >
          <Plus size={16} />
          Add User
        </button>
      </div>

      {/* Error Banner */}
      {usersError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertTriangle className="h-5 w-5 text-red-600 mr-2" />
            <div>
              <h3 className="text-sm font-medium text-red-800">Error Loading Users</h3>
              <p className="text-sm text-red-700">{usersError}</p>
            </div>
            <button
              onClick={fetchUsers}
              className="ml-auto text-red-600 hover:text-red-800 text-sm underline"
            >
              Retry
            </button>
          </div>
        </div>
      )}

      {/* Users Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {usersLoading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading users...</p>
          </div>
        ) : users.length === 0 ? (
          <div className="p-8 text-center">
            <Users className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No users found</h3>
            <p className="text-gray-600">Get started by adding your first user.</p>
          </div>
        ) : (
          <>
            {/* Desktop Table View */}
            <div className="hidden lg:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Login</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {users.map((user) => (
                    <tr key={user.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <img
                              className="h-10 w-10 rounded-full bg-gray-200 border-2 border-gray-200"
                              src={`https://ui-avatars.com/api/?name=${encodeURIComponent(user.name + ' ' + user.surname)}&background=00C5CB&color=fff`}
                              alt={user.name}
                            />
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">{user.name} {user.surname}</div>
                            <div className="text-sm text-gray-500">{user.company || 'No company'}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{user.email}</div>
                        <div className="text-sm text-gray-500">{user.mobile || 'No phone'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-purple-100 text-purple-800">
                          {user.role || 'No role'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                          user.is_active === false ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                        }`}>
                          {user.is_active === false ? 'Inactive' : 'Active'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {user.last_login ? new Date(user.last_login).toLocaleDateString() : 'Never'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                        <button
                          onClick={() => editUser(user)}
                          className="text-cyan-600 hover:text-cyan-900 inline-flex items-center px-3 py-2 text-sm"
                          title="Edit User"
                        >
                          <Edit3 size={16} className="mr-1" />
                          Edit
                        </button>
                        <button
                          onClick={() => toggleUserActive(user)}
                          className={`inline-flex items-center px-3 py-2 text-sm ${
                            user.is_active === false
                              ? 'text-green-600 hover:text-green-900'
                              : 'text-red-600 hover:text-red-900'
                          }`}
                          title={user.is_active === false ? 'Reactivate User' : 'Deactivate User'}
                        >
                          {user.is_active === false
                            ? <UserCheck size={16} className="mr-1" />
                            : <UserX size={16} className="mr-1" />
                          }
                          {user.is_active === false ? 'Reactivate' : 'Deactivate'}
                        </button>
                        <button
                          onClick={() => setDeleteConfirmUser(user)}
                          className="text-red-800 hover:text-red-900 inline-flex items-center px-3 py-2 text-sm"
                          title="Permanently Delete User"
                        >
                          <Trash2 size={16} className="mr-1" />
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Card View */}
            <div className="lg:hidden space-y-4 p-4">
              {users.map((user) => (
                <div key={user.id} className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0">
                      <img
                        className="h-12 w-12 rounded-full bg-gray-200 border-2 border-gray-200"
                        src={`https://ui-avatars.com/api/?name=${encodeURIComponent(user.name + ' ' + user.surname)}&background=00C5CB&color=fff`}
                        alt={user.name}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-gray-900">{user.name} {user.surname}</h3>
                          <p className="text-sm text-gray-600">{user.company || 'No company'}</p>
                        </div>
                        <div className="flex flex-col items-end space-y-1 ml-4">
                          <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-purple-100 text-purple-800">
                            {user.role || 'No role'}
                          </span>
                          <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                            user.is_active === false ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                          }`}>
                            {user.is_active === false ? 'Inactive' : 'Active'}
                          </span>
                        </div>
                      </div>
                      <div className="mt-3 space-y-1">
                        <div className="flex items-center text-sm text-gray-600">
                          <Mail size={12} className="mr-2 text-gray-400" />
                          <span className="truncate">{user.email}</span>
                        </div>
                        {user.mobile && (
                          <div className="flex items-center text-sm text-gray-600">
                            <Phone size={12} className="mr-2 text-gray-400" />
                            <span>{user.mobile}</span>
                          </div>
                        )}
                        <div className="flex items-center text-sm text-gray-600">
                          <Clock size={12} className="mr-2 text-gray-400" />
                          <span>Last login: {user.last_login ? new Date(user.last_login).toLocaleDateString() : 'Never'}</span>
                        </div>
                      </div>
                      <div className="mt-4 flex space-x-2">
                        <button
                          onClick={() => editUser(user)}
                          className="flex-1 inline-flex items-center justify-center px-3 py-2 text-sm font-medium text-cyan-600 bg-cyan-50 hover:bg-cyan-100 rounded-md transition-colors"
                        >
                          <Edit3 size={16} className="mr-2" />
                          Edit
                        </button>
                        <button
                          onClick={() => toggleUserActive(user)}
                          className={`flex-1 inline-flex items-center justify-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                            user.is_active === false
                              ? 'text-green-600 bg-green-50 hover:bg-green-100'
                              : 'text-red-600 bg-red-50 hover:bg-red-100'
                          }`}
                        >
                          {user.is_active === false
                            ? <UserCheck size={16} className="mr-2" />
                            : <UserX size={16} className="mr-2" />
                          }
                          {user.is_active === false ? 'Reactivate' : 'Deactivate'}
                        </button>
                        <button
                          onClick={() => setDeleteConfirmUser(user)}
                          className="flex-1 inline-flex items-center justify-center px-3 py-2 text-sm font-medium text-red-800 bg-red-100 hover:bg-red-200 rounded-md transition-colors"
                        >
                          <Trash2 size={16} className="mr-2" />
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Add User Modal */}
      {showAddUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-gray-900">Add New User</h2>
                <button onClick={() => setShowAddUser(false)} className="text-gray-400 hover:text-gray-600">
                  <X size={24} />
                </button>
              </div>

              <form onSubmit={(e) => { e.preventDefault(); createUser(); }} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
                  <input type="text" name="name" value={userForm.name} onChange={handleUserFormChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
                  <input type="text" name="surname" value={userForm.surname} onChange={handleUserFormChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email Address *</label>
                  <input type="email" name="email" value={userForm.email} onChange={handleUserFormChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                  <input type="tel" name="mobile" value={userForm.mobile} onChange={handleUserFormChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Company</label>
                  <input type="text" name="company" value={userForm.company} onChange={handleUserFormChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Role *</label>
                  <select name="role" value={userForm.role} onChange={handleUserFormChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent" required>
                    <option value="Property Manager">Property Manager</option>
                    <option value="Asset Manager">Asset Manager</option>
                    <option value="Retail Manager">Retail Manager</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Password *</label>
                  <input type="password" name="password" value={userForm.password} onChange={handleUserFormChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent" minLength={6} required />
                  <p className="text-xs text-gray-500 mt-1">Minimum 6 characters</p>
                </div>
                <div className="flex gap-3 pt-4">
                  <button type="button" onClick={() => setShowAddUser(false)}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors">
                    Cancel
                  </button>
                  <button type="submit" disabled={userFormLoading}
                    className="flex-1 px-4 py-2 text-white rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50"
                    style={{ backgroundColor: '#00C5CB' }}>
                    {userFormLoading ? 'Creating...' : 'Create User'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete User Confirmation Modal */}
      {deleteConfirmUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-gray-900">Delete User Permanently</h2>
                <button onClick={() => setDeleteConfirmUser(null)} className="text-gray-400 hover:text-gray-600">
                  <X size={24} />
                </button>
              </div>
              <p className="text-gray-600 mb-2">
                Are you sure you want to <strong>permanently delete</strong> this user?
              </p>
              <p className="text-gray-800 font-medium mb-1">{deleteConfirmUser.name} {deleteConfirmUser.surname}</p>
              <p className="text-gray-500 text-sm mb-4">{deleteConfirmUser.email}</p>
              <p className="text-red-600 text-sm mb-6">This action cannot be undone.</p>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setDeleteConfirmUser(null)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => hardDeleteUser(deleteConfirmUser.id)}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
                >
                  Delete Permanently
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditUser && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-gray-900">Edit User</h2>
                <button onClick={() => setShowEditUser(false)} className="text-gray-400 hover:text-gray-600">
                  <X size={24} />
                </button>
              </div>

              <form onSubmit={(e) => { e.preventDefault(); updateUser(); }} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
                  <input type="text" name="name" value={userForm.name} onChange={handleUserFormChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
                  <input type="text" name="surname" value={userForm.surname} onChange={handleUserFormChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email Address *</label>
                  <input type="email" name="email" value={userForm.email} onChange={handleUserFormChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                  <input type="tel" name="mobile" value={userForm.mobile} onChange={handleUserFormChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Company</label>
                  <input type="text" name="company" value={userForm.company} onChange={handleUserFormChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Role *</label>
                  <select name="role" value={userForm.role} onChange={handleUserFormChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent" required>
                    <option value="Property Manager">Property Manager</option>
                    <option value="Asset Manager">Asset Manager</option>
                    <option value="Retail Manager">Retail Manager</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
                  <input type="password" name="password" value={userForm.password} onChange={handleUserFormChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent" minLength={6} />
                  <p className="text-xs text-gray-500 mt-1">Leave blank to keep current password</p>
                </div>
                <div className="flex gap-3 pt-4">
                  <button type="button" onClick={() => setShowEditUser(false)}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors">
                    Cancel
                  </button>
                  <button type="submit" disabled={userFormLoading}
                    className="flex-1 px-4 py-2 text-white rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50"
                    style={{ backgroundColor: '#00C5CB' }}>
                    {userFormLoading ? 'Updating...' : 'Update User'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersPage;
